import * as z from 'zod';
import { ArrayElement } from '../../utils/typeUtils';

/**
 * This file exists to store all type information that is common to both concepts
 * of "FormFields".  Both the formField, which is a completable field on an instance
 * of a form associated to an event step.  And also a formTemplateField which is a
 * field defined on a form template.
 */

// Zod enum of all potential "types" of form fields that can be created/used
export const FieldTypeSchema = z.enum([
  // Standard input fields
  'text',
  'date',
  'boolean',
  'numeric',
  // Non Standard input fields
  'rich_text',
  'attachment',
  // Option Selecting fields
  'dropdown',
  'checkbox',
  'radio',
  // Visual Fields
  'section',
  // Q Object Fields
  'task',
  'document',
  'event',
  'supplier',
  'user',
  'change_request',
  'design_controls',
  // Registry is similar to QObject fields, but is different in some distinct ways
  // specifically how Display names get generated which comes from a resource sub type
  // and not from a static string
  'registry',
  'training_plan',
  'document_template',
  'event_template',
]);

export const QObjectFieldTypes = [
  FieldTypeSchema.enum.document,
  FieldTypeSchema.enum.supplier,
  FieldTypeSchema.enum.event,
  FieldTypeSchema.enum.user,
  FieldTypeSchema.enum.change_request,
  FieldTypeSchema.enum.registry,
  FieldTypeSchema.enum.design_controls,
  FieldTypeSchema.enum.training_plan,
  FieldTypeSchema.enum.document_template,
  FieldTypeSchema.enum.event_template,
  FieldTypeSchema.enum.task,
] as const satisfies readonly FieldType[];

export const RegularFieldTypes = [
  FieldTypeSchema.enum.text,
  FieldTypeSchema.enum.rich_text,
  FieldTypeSchema.enum.date,
  FieldTypeSchema.enum.numeric,
  FieldTypeSchema.enum.boolean,
  FieldTypeSchema.enum.attachment,
  FieldTypeSchema.enum.dropdown,
  FieldTypeSchema.enum.section,
  FieldTypeSchema.enum.checkbox,
  FieldTypeSchema.enum.radio,
] as const satisfies readonly FieldType[];

export const SystemOnlyFieldTypes = [
  FieldTypeSchema.enum.supplier,
  FieldTypeSchema.enum.dropdown,
  FieldTypeSchema.enum.section,
  FieldTypeSchema.enum.registry,
] as const satisfies readonly FieldType[];

// Union type of all potential "types" of form fields that can be created/used
export type FieldType = z.infer<typeof FieldTypeSchema>;
export type QObjectFieldType = ArrayElement<typeof QObjectFieldTypes>;
export type RegularFieldType = ArrayElement<typeof RegularFieldTypes>;
export type SystemOnlyFieldType = ArrayElement<typeof SystemOnlyFieldTypes>;

export const MandatorySchema = z.boolean();
export const OrderSchema = z.number();
export const LabelSchema = z.string().trim();
export const HelptextSchema = z.string().nullable();
export const DefaultContentSchema = z.string().nullable();
export const MultiSchema = z.boolean();
export const CreatedAtSchema = z.number();
export const UpdatedAtSchema = z.number();
